import { Injectable } from "@angular/core";
import { Subscription } from "rxjs";
import { AssessmentTaskScopeChangesService } from "./assessment_task_scope_changes.service";
import { LoginService } from "./login.service";
import { ProductLicensesService } from "./product_licenses.service";
import { assessmentTaskSessionIdentity } from "./assessment_task_session.util";

/** Root-owned exact visibility refresh, independent of whichever form made the write. */
@Injectable({ providedIn: "root" })
export class AssessmentAssignmentRefreshService {
    private listeners: Subscription | null = null;
    private readonly reads = new Map<number, Subscription>();
    private readonly versions = new Map<number, number>();
    private nextVersion = 0;
    private sessionKey: string | null = null;

    constructor(private readonly scope: AssessmentTaskScopeChangesService,
        private readonly login: LoginService, private readonly licenses: ProductLicensesService) {}

    start(): void {
        if (this.listeners) return;
        this.listeners = new Subscription();
        const initialIdentity = assessmentTaskSessionIdentity(this.login.getUserSnapshot()), initialToken = this.login.getToken();
        this.sessionKey = initialIdentity && initialToken ? JSON.stringify([initialIdentity, initialToken]) : null;
        this.listeners.add(this.login.getUserUpdates().subscribe(() => {
            const identity = assessmentTaskSessionIdentity(this.login.getUserSnapshot()), token = this.login.getToken();
            const key = identity && token ? JSON.stringify([identity, token]) : null;
            if (key !== this.sessionKey) { this.cancelStaleReads(); this.sessionKey = key; }
        }));
        this.listeners.add(this.scope.watchChanges().subscribe((change) => {
            if (change.source !== "mutation" || change.productLicenseId === null) return;
            const id = change.productLicenseId;
            const identity = assessmentTaskSessionIdentity(this.login.getUserSnapshot()), token = this.login.getToken();
            if (!identity || !token) return;
            const version = ++this.nextVersion;
            this.versions.set(id, version);
            this.reads.get(id)?.unsubscribe();
            const current = () => this.versions.get(id) === version &&
                identity === assessmentTaskSessionIdentity(this.login.getUserSnapshot()) && token === this.login.getToken();
            this.reads.set(id, this.licenses.refreshAssessmentAccess(id, current).subscribe({ error: () => undefined }));
        }));
    }

    private cancelStaleReads(): void {
        // Fresh reads are triggered by committed hints in the new session.
        for (const read of this.reads.values()) read.unsubscribe();
        this.reads.clear();
        this.versions.clear();
    }

    stop(): void {
        this.listeners?.unsubscribe();
        this.listeners = null;
        this.cancelStaleReads();
    }
}
